// How a sound source's level and tone depend on where the listener is (2026-10-29): the cone and distance law a
// PannerNode would apply, an off-axis low-pass (a cabinet's shadow) and a near-field low shelf. No DOM: speakers.ts
// applies these per listener update, and the tests check them in Node.

import type { Directivity } from '@lr3d/shared';

type V3 = readonly [number, number, number];

/** Every room without audio.directivity: the panner settings used before 2026-10-29. */
export const DEFAULT_DIRECTIVITY: Directivity = { inner_deg: 120, outer_deg: 300, outer_gain: 0.45, ref_m: 1.2, rolloff: 0.8 };

/** PannerNode maxDistance, kept from before; a room's audio.directivity.max_m replaces it. */
export const MAX_DISTANCE_M = 30;

/** Angle (deg, 0..180) between `forward` and the direction from `from` to `to`; 0 when they coincide. */
export function offAxisDeg(from: V3, forward: V3, to: V3): number {
  const dx = to[0] - from[0], dy = to[1] - from[1], dz = to[2] - from[2];
  const d = Math.hypot(dx, dy, dz), f = Math.hypot(forward[0], forward[1], forward[2]);
  if (d < 1e-6 || f < 1e-6) return 0;
  const c = (dx * forward[0] + dy * forward[1] + dz * forward[2]) / (d * f);
  return (Math.acos(Math.max(-1, Math.min(1, c))) * 180) / Math.PI;
}

/** The Web Audio cone gain (PannerNode spec): 1 inside inner/2, outer_gain beyond outer/2, linear between. */
export function coneGain(d: Directivity, angleDeg: number): number {
  const a = d.inner_deg / 2, b = d.outer_deg / 2;
  if (angleDeg <= a) return 1;
  if (angleDeg >= b) return d.outer_gain;
  const x = (angleDeg - a) / (b - a);
  return 1 - x + d.outer_gain * x;
}

/** The Web Audio inverse distance law, distance clamped to ref .. max (MAX_DISTANCE_M by default). */
export function distanceGain(ref: number, rolloff: number, distance: number, max = MAX_DISTANCE_M): number {
  const d = Math.min(max, Math.max(ref, distance));
  return ref / (ref + rolloff * (d - ref));
}

/**
 * The off-axis low-pass (Hz): lowpass_hz[0] inside the inner cone, [1] at 90 deg, [2] at 180 deg, linear in log
 * frequency between; Infinity without one.
 */
export function offAxisLowpass(d: Directivity, angleDeg: number): number {
  const lp = d.lowpass_hz;
  if (!lp) return Infinity;
  const a = Math.min(d.inner_deg / 2, 89.9);
  const lerp = (f0: number, f1: number, x: number) => Math.exp(Math.log(f0) + (Math.log(f1) - Math.log(f0)) * Math.max(0, Math.min(1, x)));
  if (angleDeg <= a) return lp[0];
  if (angleDeg <= 90) return lerp(lp[0], lp[1], (angleDeg - a) / (90 - a));
  return lerp(lp[1], lp[2], (angleDeg - 90) / 90);
}

/** The near-field low shelf's gain (dB) at `distance` from the sources' centre: gain_db at from_m or nearer, 0 at to_m. */
export function nearShelfDb(d: Directivity, distance: number): number {
  const n = d.near;
  if (!n) return 0;
  const x = (distance - n.from_m) / (n.to_m - n.from_m);
  return n.gain_db * (1 - Math.max(0, Math.min(1, x)));
}

/** The mean of the sources' positions and the normalised mean of their forward vectors. */
export function centreOf(sources: readonly { position: V3; forward: V3 }[]): { position: [number, number, number]; forward: [number, number, number] } {
  const p: [number, number, number] = [0, 0, 0], f: [number, number, number] = [0, 0, 0];
  for (const s of sources) for (let i = 0; i < 3; i++) { p[i]! += s.position[i]! / sources.length; f[i]! += s.forward[i]!; }
  const n = Math.hypot(...f) || 1;
  return { position: p, forward: [f[0] / n, f[1] / n, f[2] / n] };
}
